import { useEffect } from "react";
import { apiFetch } from "@/lib/api";
import { installPublicStationSortOrder } from "@/lib/sgsProductionCalendarStationOrder";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useLocation } from "react-router-dom";

import { S7302EpcraComplianceBanner } from "@/components/mrp/S7302EpcraComplianceBanner";
import {
  fetchHubMockupDocument,
  hubMockupModuleBannerVisible,
} from "@/lib/hubMockupApi";
import { navItemsForApp } from "@/lib/appNavigation";
import { LaborDevStatusProvider } from "@/lib/labor/laborDevStatusPanelContext";
import { HubModuleKeyProvider } from "@/lib/hubModuleKeyContext";
import { useCoHomedLayoutApp } from "@/lib/useCoHomedLayoutApp";
import { useNavigationTrail } from "@/lib/navigationTrailContext";
import { SchedulerStatus } from "./SchedulerStatus";

import { CurrentPagePresence } from "@/components/CurrentPagePresence";
import { QcIncidentModalHost } from "@/components/qc/QcIncidentModalHost";
import { QcIncidentModalProvider } from "@/lib/qc/qcIncidentModalContext";
import { AppSwitcher } from "./AppSwitcher";
import { HubMockupBadge } from "@/components/HubMockupBadge";
import { ModuleGroupedNav } from "./ModuleGroupedNav";
import { LaborReadOnlyStayNotice } from "./LaborReadOnlyStayNotice";
import { FieldSearcher } from "./FieldSearcher";
import { QuickSwitcher } from "./QuickSwitcher";
import { UserMenu } from "./UserMenu";
import {
  hubPageShellWidthClassName,
  isHubWorkspaceHeightMode,
  isKittingPickPath,
  isQualityPrintDocumentPath,
  isSalesOrderEditorPath,
  resolveHubPageLayoutPolicy,
} from "@/lib/hubPageLayoutPolicy";
import { hubAppShellHeaderClassName } from "@/lib/hubAppChrome";
import { cn } from "@/lib/utils";
import { useHubDevOverlayInset } from "@/lib/useHubDevOverlayInset";
import { isSgsLauncherAvailable } from "@/lib/sgsLauncher";

export function Layout({
  app: routeApp,
}: {
  app:
    | "erp"
    | "mrp"
    | "bom"
    | "admin"
    | "clampqc"
    | "manufacturing"
    | "manufacturing_management"
    | "documents"
    | "wiki"
    | "shipping"
    | "warehouse"
    | "accounting"
    | "quality"
    | "marketing"
    | "art_and_labels"
    | "wireless"
    | "inside_sales"
    | "commissions"
    | "printing"
    | "engineering"
    | "people"
    | "personal";
}) {
  const stationOrderQuery = useQuery({
    queryKey: ["public-stations"],
    queryFn: () =>
      apiFetch<{
        stations: { id: number; name: string; sortOrder: number | null }[];
      }>("/api/zpl-printers/stations"),
    staleTime: 120_000,
  });
  useEffect(() => {
    if (stationOrderQuery.data?.stations != null) {
      installPublicStationSortOrder(stationOrderQuery.data.stations);
    }
  }, [stationOrderQuery.data]);
  const { pathname } = useLocation();
  const app = useCoHomedLayoutApp(routeApp);
  const { resetNavigationTrail } = useNavigationTrail();
  const devOverlayInset = useHubDevOverlayInset();
  const navItems = navItemsForApp(app);
  const layoutPolicy = resolveHubPageLayoutPolicy(pathname);
  const shellWidth = hubPageShellWidthClassName(layoutPolicy.widthTier);
  const kittingPickTabletWebView = isKittingPickPath(pathname) && isSgsLauncherAvailable();
  const salesOrderEditor = isSalesOrderEditorPath(pathname);
  const workspaceHeight = isHubWorkspaceHeightMode(layoutPolicy.heightMode);
  const hideChromeOnPrint = isQualityPrintDocumentPath(pathname);
  const reserveDevOverlaySpace = !salesOrderEditor;

  useEffect(() => {
    if (!workspaceHeight) {
      return;
    }
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const previous = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      rootOverflow: root?.style.overflow ?? "",
    };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root != null) {
      root.style.overflow = "hidden";
    }
    return () => {
      html.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
      if (root != null) {
        root.style.overflow = previous.rootOverflow;
      }
    };
  }, [workspaceHeight]);

  const hubNavMockup = useQuery({
    queryKey: ["hub-mockup", "document"],
    queryFn: () => fetchHubMockupDocument(),
    staleTime: 60_000,
    enabled: app === "mrp" || app === "manufacturing" || app === "manufacturing_management" || app === "clampqc",
  });
  const hubNavDocument = hubNavMockup.data?.document;

  return (
    <HubModuleKeyProvider moduleKey={app}>
      <LaborDevStatusProvider>
      <QcIncidentModalProvider>
      <div
        data-hub-height-mode={layoutPolicy.heightMode}
        className={cn(
          "hub-app-shell flex flex-col bg-slate-50",
          workspaceHeight ? "overflow-hidden" : undefined,
        )}
      >
      <header
        className={cn(
          hubAppShellHeaderClassName,
          hideChromeOnPrint && "print:hidden",
          kittingPickTabletWebView && "hidden",
        )}
      >
        <div className={cn("mx-auto flex w-full items-center gap-2 py-2 sm:gap-3", shellWidth)}>
          <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-6">
            <AppSwitcher activeKey={app} />
            {app === "clampqc" && hubMockupModuleBannerVisible(hubNavDocument, "clampqc") ? (
              <HubMockupBadge variant="nav" />
            ) : null}
            <ModuleGroupedNav
              app={app}
              navItems={navItems}
              pathname={pathname}
              hubNavDocument={hubNavDocument}
              onNavigate={resetNavigationTrail}
            />
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-2.5">
            {app === "erp" ? <SchedulerStatus /> : null}
            <CurrentPagePresence />
            <QuickSwitcher />
            <UserMenu />
          </div>
        </div>
      </header>
      <main
        className={cn(
          "hub-main mx-auto w-full flex-1",
          workspaceHeight
            ? "flex min-h-0 flex-col overflow-hidden pt-4 pb-0"
            : "py-6",
          shellWidth,
          kittingPickTabletWebView && "flex min-h-0 flex-col overflow-hidden py-0",
          hideChromeOnPrint && "print:max-w-none print:px-0 print:py-0",
        )}
        style={reserveDevOverlaySpace && (workspaceHeight ? devOverlayInset.clearancePx : devOverlayInset.scrollPaddingPx) > 0
          ? {
              paddingBottom: workspaceHeight
                ? `${devOverlayInset.clearancePx}px`
                : `calc(1.5rem + ${devOverlayInset.scrollPaddingPx}px)`,
            }
          : undefined}
      >
        <LaborReadOnlyStayNotice />
        {app === "mrp" ||
        (app === "manufacturing" &&
          (pathname.startsWith("/manufacturing/pre-encapsulation") ||
            pathname.startsWith("/manufacturing/encapsulation"))) ||
        (app === "manufacturing_management" && pathname.startsWith("/manufacturing-management/potting-po")) ? (
          <S7302EpcraComplianceBanner className="mb-4" />
        ) : null}
        <Outlet />
      </main>
      <div className={hideChromeOnPrint ? "print:hidden" : undefined}>
        <FieldSearcher />
      </div>
      <QcIncidentModalHost />
    </div>
      </QcIncidentModalProvider>
      </LaborDevStatusProvider>
    </HubModuleKeyProvider>
  );
}
